<template>
  <b-modal
    id="filters-modal"
    ref="modal"
    :content-class="{'total-height': isFiltersMenu || isSubFilter, 'thinner-padding': isOnlyLocationFilter || isSortFilter}"
    :title="modalTitle"
    no-close-on-esc
    @hidden="resetModal"
    @show="showRangeSlidersWithTotalWidth">

    <template #modal-header="{ close }">
      <div class="w-100">
        <div class="modal-header-content">
          <div class="d-flex align-items-center">
            <i v-if="isSubFilter" class="ph ph-caret-circle-left mt-1 mr-2 filters-modal-back-icon"
               @click="openFiltersMenu"></i>
            <h1 class="page-title">{{ modalTitle }}</h1>
          </div>

          <div class="filters-modal-close-icon" @click="close()">
            <i class="ph-x"/>
          </div>
        </div>
      </div>
    </template>

    <!-- modal-body -->
    <template #default="{ close }">

      <div v-if="isSortFilter">
        <b-form-radio v-for="sortObject in sortObjects" :key="sortObject.value" v-model="sortSelectedValue"
                      :value="sortObject.value"
                      class="mt-2" @change="close()">{{ $t(sortObject.i18n) }}
        </b-form-radio>
      </div>

      <div v-if="isOnlyLocationFilter" class="mt-1">
        <b-input-group
          class="mb-2 w-100 filters-search-bar"
          size="sm">
          <b-input-group-append>
            <b-input-group-text class="search-input-append">
              <i class="ph-custom ph-magnifying-glass"/>
            </b-input-group-text>
          </b-input-group-append>
          <GmapAutocomplete
            ref="addressAutocompleteOnlyLocationFilter"
            v-model="localisationSearch.value"
            :class="{'active': localisationSearch.value, 'in-error': localisationSearch.status === false}"
            :options="autocompleteOptions"
            :placeholder="$t('Research')"
            class="search-input form-control"
            @input="handleStartAddressInput"
            @place_changed="handleAddressSelect">
          </GmapAutocomplete>
        </b-input-group>

        <div v-if="localisationSearch.invalidFeedback" class="localisation-invalid-feedback">
          {{ localisationSearch.invalidFeedback }}
        </div>

        <div class="d-flex">
          <div v-if="localisationSelected.address" class="localisation-tag">
            <div class="localisation-tag-address">
              {{ localisationSelected.address }}
            </div>
            <div class="localisation-tag-km">
              {{ localisationSelected.radius }} Km {{ $t('Filters.localisation.around') }}
            </div>
          </div>
        </div>

        <div>
          <div class="special-filter-menu-line">
            <div class="range-slider-title">{{ $t('Filters.localisation.radius') }}</div>
            <div>{{ localisationRadius }} Km</div>
          </div>
          <SimpleRangeSliderField v-if="showRangeSliders" :key="localisationRadiusSliderForLocationFilterKey"
                                  :maxThreshold="limitMaxLocalisationRadius" :minThreshold="limitMinLocalisationRadius"
                                  :value="localisationRadius"
                                  @update:value="updateLocalisationRadius"></SimpleRangeSliderField>
          <div class="under-range-slider-line">
            <div>{{ limitMinLocalisationRadius }} Km</div>
            <div>{{ limitMaxLocalisationRadius }} Km</div>
          </div>
        </div>
      </div>

      <div v-if="isFiltersMenu" class="modal-filters-menu-container">
        <div v-for="filtersMenuLine in filtersMenuLines"
             :class="{'filters-menu-line-only-in-mobile-view': filtersMenuLine.isOnlyInMobileViewButAlsoWhenSearchNotCollapsed && isSearchCollapsedFollowingCollapsed}"
             class="filters-menu-line"
             @click="openSubFilter(filtersMenuLine)">
          <div>{{ filtersMenuLine.name }}</div>
          <div class="filters-menu-line-selected-values">{{ filtersMenuLine.displaySelectedValuesText }}</div>
        </div>
        <div>
          <div class="special-filter-menu-line">
            <div class="range-slider-title">{{ $t('annonce.vehicle.engine.Power') }}</div>
            <div>{{ minFiscalPower }}-{{ maxFiscalPower }}{{
                maxFiscalPower === limitMaxFiscalPower ? '+' : ''
              }}Cv
            </div>
          </div>
          <DoubleRangeSliderField v-if="showRangeSliders" :key="fiscalPowerSliderKey"
                                  :max="maxFiscalPower" :maxThreshold="limitMaxFiscalPower"
                                  :min="minFiscalPower" :minThreshold="limitMinFiscalPower"
                                  @update:min="updateMinFiscalPower"
                                  @update:max="updateMaxFiscalPower"></DoubleRangeSliderField>
        </div>
      </div>

      <div v-if="isSubFilter && subFilterObjectType !== 'localisation'"
           class="modal-sub-filter-container d-flex flex-column">
        <b-input-group
          class="mb-2 w-100 filters-search-bar mt-3"
          size="sm">
          <b-input-group-append>
            <b-input-group-text class="search-input-append">
              <i class="ph-custom ph-magnifying-glass"/>
            </b-input-group-text>
          </b-input-group-append>
          <b-form-input
            ref="searchInputSubFilter"
            v-model="subFilterSearch"
            :class="{'active': subFilterSearch}"
            :placeholder="$t('Research')"
            class="search-input"
            maxlength="250"
            type="search">
          </b-form-input>
        </b-input-group>

        <div v-if="!noElementToShow" class="d-flex justify-content-end">
          <div class="sub-filter-yellow-action" @click="selectOrUnselectAll">
            {{ canSelectAll ? $t('Filters.select.all') : $t('Filters.unselect.all') }}
          </div>
        </div>

        <div class="sub-filter-list-container">
          <!-- sub filter NO LINE -->
          <div v-if="noElementToShow && subFilterObjectType">
            <div class="text-center mb-4 mt-5">
              <img :src="require(`@/assets/images/b122/img-confused.png`)" alt="no result" class="empty-state-img mb-2">
              <div class="sub-filter-list-no-result-title">{{
                  $t('Filters.no.values.found.for.' + subFilterObjectType)
                }}
              </div>
              <div class="sub-filter-list-no-result-text">{{
                  subFilterSearch && filtersMenuObjects[subFilterObjectType].isEnumMenu ? $t('Filters.no.values.found.check.search') : (subFilterSearch ? $t('Filters.no.values.found.check.search.or.filters') : $t('Filters.no.values.found.check.filters'))
                }}
              </div>
            </div>
          </div>
          <!-- sub filter lines -->
          <div v-for="(subFilterElement, subFilterElementIndex) in subFilterList" v-if="subFilterElement.isVisible"
               :key="subFilterKey(subFilterElement.enumValue, subFilterElement.range)"
               :class="{'sub-filter-list-element-container-higher-category': subFilterElement.isHigherCategory, 'sub-filter-list-element-container-last': subFilterElementIndex !== (subFilterList.length - 1)}"
               class="sub-filter-list-element-container">
            <div class="sub-filter-list-element">
              <div class="sub-filter-list-element-inside"
                   @click="toggleSelectSubFilterElement(subFilterElement, false)">
                <div>{{ subFilterElement.name }}</div>
                <div>
                  <b-form-checkbox
                    v-model=subFilterElement.isSelected
                    button-variant="secondary"
                    class="sub-filter-list-element-checkbox"
                    @change="toggleSelectSubFilterElement(subFilterElement, true)"
                    @click.native.stop>
                  </b-form-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isSubFilter && subFilterObjectType === 'localisation'">
        <b-input-group
          class="mb-2 w-100 filters-search-bar mt-3"
          size="sm">
          <b-input-group-append>
            <b-input-group-text class="search-input-append">
              <i class="ph-custom ph-magnifying-glass"/>
            </b-input-group-text>
          </b-input-group-append>
          <GmapAutocomplete
            ref="addressAutocompleteSubFilter"
            v-model="localisationSearch.value"
            :class="{'active': localisationSearch.value, 'in-error': localisationSearch.status === false}"
            :options="autocompleteOptions"
            :placeholder="$t('Research')"
            class="search-input form-control"
            @input="handleStartAddressInput"
            @place_changed="handleAddressSelect">
          </GmapAutocomplete>
        </b-input-group>

        <div v-if="localisationSearch.invalidFeedback" class="localisation-invalid-feedback">
          {{ localisationSearch.invalidFeedback }}
        </div>

        <div class="d-flex">
          <div v-if="localisationSelected.address" class="localisation-tag">
            <div class="localisation-tag-address">
              {{ localisationSelected.address }}
            </div>
            <div class="localisation-tag-km">
              {{ localisationSelected.radius }} Km {{ $t('Filters.localisation.around') }}
            </div>
          </div>
        </div>

        <div>
          <div class="special-filter-menu-line">
            <div class="range-slider-title">{{ $t('Filters.localisation.radius') }}</div>
            <div>{{ localisationRadius }} Km</div>
          </div>
          <SimpleRangeSliderField v-if="showRangeSliders" :key="localisationRadiusSliderForSubFilterKey"
                                  :maxThreshold="limitMaxLocalisationRadius" :minThreshold="limitMinLocalisationRadius"
                                  :value="localisationRadius"
                                  @update:value="updateLocalisationRadius"></SimpleRangeSliderField>
          <div class="under-range-slider-line">
            <div>{{ limitMinLocalisationRadius }} Km</div>
            <div>{{ limitMaxLocalisationRadius }} Km</div>
          </div>
        </div>
      </div>

    </template>

    <template #modal-footer="{ close }">
      <div class="w-100 footer-element">

        <div v-if="isOnlyLocationFilter" class="row mt-3">
          <div class="col-6 pr-1">
            <b-button class="w-100 btn-border-more-width btn-height-thinner" variant="secondary"
                      @click="cleanLocalisationFilter()">{{ $t('Erase') }}
            </b-button>
          </div>
          <div class="col-6 pl-1">
            <b-button class="w-100 btn-border-more-width btn-height-thinner" variant="primary"
                      @click="showFeedBackLocalisation()">
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>

        <div v-if="isFiltersMenu">
          <b-button class="w-100 btn-border-more-width btn-height-thinner mt-3" variant="secondary"
                    @click="cleanAllFilters()">{{ $t('Erase.all') }}
          </b-button>
          <b-button class="w-100 btn-border-more-width btn-height-thinner mt-3" variant="primary" @click="close()">
            {{ $t('Search') }}
          </b-button>
        </div>

        <div v-if="isSubFilter" class="row mt-3">
          <div class="col-6 pr-1">
            <b-button class="w-100 btn-border-more-width btn-height-thinner" variant="secondary"
                      @click="cleanSubFilter()">{{ $t('Erase') }}
            </b-button>
          </div>
          <div class="col-6 pl-1">
            <b-button class="w-100 btn-border-more-width btn-height-thinner" variant="primary"
                      @click="close()">
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>

      </div>
    </template>

  </b-modal>
</template>

<script>
import api from "../../services/api";
import i18n from "../../i18n";
import SimpleRangeSliderField from "../../components/b122/editFields/SimpleRangeSliderField";
import DoubleRangeSliderField from "../../components/b122/editFields/DoubleRangeSliderField";
import {eventBus} from "../../main";

export default {

  name: "FiltersModal",

  components: {
    SimpleRangeSliderField,
    DoubleRangeSliderField,
  },

  props: {
    fromBtnName: String,
    filtersDataInitial: null,
    needsToGetFiltersDataFromApp: false,
  },

  data() {
    return {
      isSortFilter: false,
      isOnlyLocationFilter: false,
      isFiltersMenu: false,
      isSubFilter: false,

      defaultModalTitle: this.$t("Filters.filters"),
      defaultSortModalTitle: this.$t("Filters.sort"),
      defaultLocalisationModalTitle: this.$t("Filters.localisation"),
      modalTitle: this.$t("Filters.filters"),

      subFilterObjectType: null,

      filtersMenuObjects: {
        localisation: {
          name: this.$t("Filters.localisation"),
          displaySelectedValuesText: null,
          filterObjectType: 'localisation',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: true,
          isEnumMenu: null,
          isHightCategoryFor: null,
          isFilteringOnObjectTypes: null,
        },
        vehicleType: {
          name: this.$t("Vehicle"),
          displaySelectedValuesText: null,
          filterObjectType: 'vehicleType',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: false,
          isEnumMenu: false,
          isHightCategoryFor: null,
          isFilteringOnObjectTypes: ['brand', 'model', 'modelFinish'],
        },
        brand: {
          name: this.$t("VehicleBrand"),
          displaySelectedValuesText: null,
          filterObjectType: 'brand',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: false,
          isEnumMenu: false,
          isHightCategoryFor: 'model',
          isFilteringOnObjectTypes: ['model', 'modelFinish'],
        },
        model: {
          name: this.$t("VehicleModel"),
          displaySelectedValuesText: null,
          filterObjectType: 'model',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: false,
          isEnumMenu: false,
          isHightCategoryFor: 'modelFinish',
          isFilteringOnObjectTypes: ['modelFinish'],
        },
        modelFinish: {
          name: this.$t("vehicleModel.Finishes"),
          displaySelectedValuesText: null,
          filterObjectType: 'modelFinish',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: false,
          isEnumMenu: false,
          isHightCategoryFor: null,
          isFilteringOnObjectTypes: null,
        },
        color: {
          name: this.$t("Colors"),
          displaySelectedValuesText: null,
          filterObjectType: 'color',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: false,
          isEnumMenu: true,
          isHightCategoryFor: null,
          isFilteringOnObjectTypes: null,
        },
        energy: {
          name: this.$t("annonce.vehicle.Energy"),
          displaySelectedValuesText: null,
          filterObjectType: 'energy',
          isOnlyInMobileViewButAlsoWhenSearchNotCollapsed: false,
          isEnumMenu: true,
          isHightCategoryFor: null,
          isFilteringOnObjectTypes: null,
        },
      },

      showRangeSliders: false, //doit être à true si filtersModal est totalement ouverte, false sinon
      fiscalPowerSliderKey: 0,
      localisationRadiusSliderForSubFilterKey: 0,
      localisationRadiusSliderForLocationFilterKey: 0,

      debounceSearchAnnonces: null,
      isCurrentInit: true,
      isUpdatingOrDeletingSeveralFilters: false,

      sortObjects: [],
      sortSelectedValue: null,

      limitMinFiscalPower: 0,
      limitMaxFiscalPower: 30,
      minFiscalPower: 0,
      maxFiscalPower: 30,

      search: null,
      isSearchCollapsedFollowingCollapsed: true,

      autocompleteOptions: {
        componentRestrictions: {
          country: [
            'fr',
            this.$store.state.currentUserCountryCode
          ],
        },
      },
      localisationSearch: {
        value: '',
        invalidFeedback: null,
        status: null,
      },
      limitMinLocalisationRadius: 1,
      limitMaxLocalisationRadius: 200,
      localisationRadius: 1,
      localisationSelected: {
        address: null,
        radius: null,
        latitude: null,
        longitude: null,
      },

      subFilterSearch: '', //si il y a une valeur ici, aucune higherCategory dans les propositions !

      canSelectAll: true,

      //système de tous les sous-filtres
      subFilterObjects: {},

      //système des sous-filtres enum qui ont une réserve avec toutes les données en plus
      subFilterObjectsColors: {},
      subFilterObjectsEnergies: {},
      settingIsVisible: false,
      oneIsVisible: false,
      //...et des keys pour refresh l'affichage des éléments
      subFilterKeysColors: {},
      subFilterKeysEnergies: {},
      //...et un reload complet initial pour que l'affichage des éléments se refresh tout seul aux clicks des isSelected une fois fait
      firstReloadSubFilterObjectsForEnumColorsDone: false,
      firstReloadSubFilterObjectsForEnumEnergiesDone: false,

      subFiltersValues: {
        vehicleType: {
          ids: [],
          textValueIfOne: null,
        },
        brand: {
          ids: [],
          textValueIfOne: null,
        },
        model: {
          ids: [],
          higherCategoryIds: [],
        },
        modelFinish: {
          ids: [],
          higherCategoryIds: [],
        },
        color: {
          values: [],
        },
        energy: {
          values: [],
        },
      },
    };
  },

  created() {
    eventBus.$on('openFiltersModalFromSameFromBtnName', this.openFiltersModalFromValueFromBtnName)
    eventBus.$on('initFiltersDataInFiltersModal', this.initFiltersData)
    eventBus.$on('updateIsSearchCollapsedFollowingCollapsed', this.updateIsSearchCollapsedFollowingCollapsed)
    this.debounceSearchAnnonces = this.debounce((newValue, oldValue) => {
      this.searchAnnoncesFromNewFilters()
    }, 500)
  },

  destroyed() {
    eventBus.$off('openFiltersModalFromSameFromBtnName', this.openFiltersModalFromValueFromBtnName)
    eventBus.$off('initFiltersDataInFiltersModal', this.initFiltersData)
    eventBus.$off('updateIsSearchCollapsedFollowingCollapsed', this.updateIsSearchCollapsedFollowingCollapsed)
  },

  async mounted() {
    await this.initSortDataEnum()
    await this.initSubFilterDataEnum()
    await this.initFiltersData(this.filtersDataInitial, true)
    this.isCurrentInit = false

    if (this.needsToGetFiltersDataFromApp) {
      eventBus.$emit("initElseResetFiltersValues")
    }

    if (this.fromBtnName) {
      this.openFiltersModalFromValueFromBtnName()
      this.showFiltersModal()
    }
  },

  watch: {

    "fromBtnName": function (newVal) {
      this.openFiltersModalFromValueFromBtnName()
    },

    "localisationSelected": function (newVal) {
      if (this.localisationSelected.address) {
        this.filtersMenuObjects.localisation.displaySelectedValuesText = this.localisationSelected.address + ' - ' + this.localisationSelected.radius + 'Km'
      } else {
        this.filtersMenuObjects.localisation.displaySelectedValuesText = ""
      }
    },

    "subFilterSearch": function (newVal) {
      if (this.isUpdatingOrDeletingSeveralFilters || !this.subFilterObjectType) {
        return
      }
      this.refreshSubFilterData(this.subFilterObjectType)
    },

    "subFilterObjects": function (newVal) {
      if (this.settingIsVisible) {
        return
      }
      if (this.subFilterObjectType === 'color') {
        Object.keys(this.subFilterObjects).forEach(key => {
          var keyAsRangeNumber = parseInt(key)
          this.subFilterObjectsColors[keyAsRangeNumber].isSelected = this.subFilterObjects[keyAsRangeNumber].isSelected
        });

      } else if (this.subFilterObjectType === 'energy') {
        Object.keys(this.subFilterObjects).forEach(key => {
          var keyAsRangeNumber = parseInt(key)
          this.subFilterObjectsEnergies[keyAsRangeNumber].isSelected = this.subFilterObjects[keyAsRangeNumber].isSelected
        });
      }
    },

    "filtersData": function (newVal, oldVal) {
      if (this.isCurrentInit) {
        return
      }
      this.debounceSearchAnnonces(newVal, oldVal) //debounce pour ne pas aller charger des annonces si les modifications sont trop rapprochées
    },

  },

  computed: {
    filtersMenuLines() {
      return Object.entries(this.filtersMenuObjects).map((entry) => (entry[1]));
    },
    subFilterList() {
      return Object.entries(this.subFilterObjects).map((entry) => (entry[1]));
    },
    subFilterKey() {
      return (enumValue, range) => {
        if (this.subFilterObjectType === 'color') {
          return this.subFilterObjectType + enumValue + this.subFilterKeysColors[enumValue]
        }
        if (this.subFilterObjectType === 'energy') {
          return this.subFilterObjectType + enumValue + this.subFilterKeysEnergies[enumValue]
        }
        return this.subFilterObjectType + range
      }
    },
    noElementToShow() {
      return this.subFilterList.length === 0 || (!this.oneIsVisible && this.filtersMenuObjects[this.subFilterObjectType].isEnumMenu)
    },
    filtersData() {
      return {
        researchSorting: this.sortSelectedValue,
        address: this.localisationSelected.address,
        locationRadius: this.localisationSelected.radius,
        latitude: this.localisationSelected.latitude,
        longitude: this.localisationSelected.longitude,
        vehicles: this.toStringApiList(this.subFiltersValues.vehicleType.ids),
        brands: this.toStringApiList(this.subFiltersValues.brand.ids),
        models: this.toStringApiList(this.subFiltersValues.model.ids),
        modelFinishes: this.toStringApiList(this.subFiltersValues.modelFinish.ids),
        colors: this.toStringApiList(this.subFiltersValues.color.values),
        energies: this.toStringApiList(this.subFiltersValues.energy.values),
        modelsHigherCategoryIds: this.toStringApiList(this.subFiltersValues.model.higherCategoryIds),
        modelFinishesHigherCategoryIds: this.toStringApiList(this.subFiltersValues.modelFinish.higherCategoryIds),
        minFiscalPower: this.minFiscalPower,
        maxFiscalPower: (this.maxFiscalPower === this.limitMaxFiscalPower ? null : this.maxFiscalPower),
        isSelectedFiscalPowerFilter: !(this.minFiscalPower === this.limitMinFiscalPower && this.maxFiscalPower === this.limitMaxFiscalPower),
        selectedFilterVehicleTypeNameIfOne: this.subFiltersValues.vehicleType.textValueIfOne,
        selectedFilterBrandNameIfOne: this.subFiltersValues.brand.textValueIfOne,
      }
    },
  },

  methods: {

    showFeedBackLocalisation() {
      if ((!this.localisationSelected || !this.localisationSelected.address) &&
        (!this.localisationSearch.value || this.localisationSearch.value.trim() === "")) {
        this.localisationSearch.invalidFeedback = this.$t('search.localisation.invalidFeedback')
      } else {
        this.localisationSearch.invalidFeedback = null
        this.$bvModal.hide('filters-modal')
      }
    },

    showFiltersModal() {
      this.$bvModal.show('filters-modal')
    },

    async initSortDataEnum() {
      await api({
        method: 'POST',
        url: `/search/getSortObjectsForRadioButtons`,
        data: {}
      })
        .then((response) => {
          this.sortObjects = response.data.sortObjects
          this.sortSelectedValue = response.data.sortSelectedValue
        })
    },

    async initSubFilterDataEnum() {
      //color
      const dataColor = new FormData()
      dataColor.append('objectType', 'color')

      await api({
        method: 'POST',
        url: `/search/getSubFilterObjectsForOptions`,
        data: dataColor
      })
        .then((response) => {
          var subFilterListColors = response.data

          //get trads
          subFilterListColors.map((filterObject) => {
            filterObject.name = i18n.t(filterObject.name.toString())
          })
          subFilterListColors.sort((a, b) => {
            if (a.name > b.name) {
              return 1
            }
            if (a.name < b.name) {
              return -1
            }
            return 0
          })

          this.subFilterObjectsColors = {}
          this.subFilterKeysColors = {}
          var range = 0
          for (const colorInfos of subFilterListColors) {
            colorInfos.range = range
            this.subFilterObjectsColors[range] = colorInfos
            //init isSelected from setted values from filtersData from App.vue
            this.subFilterObjectsColors[range].isSelected = this.subFiltersValues.color.values.includes(colorInfos.enumValue)

            this.subFilterKeysColors[colorInfos.enumValue] = 0
            range++
          }
        })

      //energy
      const dataEnergy = new FormData()
      dataEnergy.append('objectType', 'energy')

      await api({
        method: 'POST',
        url: `/search/getSubFilterObjectsForOptions`,
        data: dataEnergy
      })
        .then((response) => {
          var subFilterListEnergies = response.data

          //get trads
          subFilterListEnergies.map((filterObject) => {
            filterObject.name = i18n.t(filterObject.name.toString())
          })
          subFilterListEnergies.sort((a, b) => {
            if (a.name > b.name) {
              return 1
            }
            if (a.name < b.name) {
              return -1
            }
            return 0
          })

          this.subFilterObjectsEnergies = {}
          this.subFilterKeysEnergies = {}
          var range = 0
          for (const energyInfos of subFilterListEnergies) {
            energyInfos.range = range
            this.subFilterObjectsEnergies[range] = energyInfos
            //init isSelected from setted values from filtersData from App.vue
            this.subFilterObjectsEnergies[range].isSelected = this.subFiltersValues.energy.values.includes(energyInfos.enumValue)

            this.subFilterKeysEnergies[energyInfos.enumValue] = 0
            range++
          }
        })
    },

    /** this.filtersDataInitial est initialisé à la création du component Search.vue, et provient des valeurs sauvées dans le component App.vue
     * si on accède à Search.vue par un autre moyen, filtersData correspond aux données récupérées directement dans App.vue */
    async initFiltersData(filtersData, fromMounted = false) {

      if (filtersData === null) {
        //recherche simple sans modifier les paramètres
        if (!fromMounted) {
          this.searchAnnoncesFromNewFilters()
        }
        return
      }

      //1) sortSelectedValue
      this.sortSelectedValue = filtersData.researchSorting

      //2) FiscalPower
      this.minFiscalPower = filtersData.minFiscalPower
      this.maxFiscalPower = (filtersData.maxFiscalPower === null ? this.limitMaxFiscalPower : filtersData.maxFiscalPower)

      //3) Localisation
      this.localisationRadius = (filtersData.locationRadius === null ? this.limitMinLocalisationRadius : filtersData.locationRadius)
      this.localisationSelected = {
        address: filtersData.address,
        radius: filtersData.locationRadius,
        latitude: filtersData.latitude,
        longitude: filtersData.longitude,
      }

      //4) set les isSelected
      var colorsValues = JSON.parse(filtersData.colors)
      var energiesValues = JSON.parse(filtersData.energies)

      Object.keys(this.subFilterObjectsColors).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        this.subFilterObjectsColors[keyAsRangeNumber].isSelected = colorsValues.includes(this.subFilterObjectsColors[keyAsRangeNumber].enumValue)
      });

      Object.keys(this.subFilterObjectsEnergies).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        this.subFilterObjectsEnergies[keyAsRangeNumber].isSelected = energiesValues.includes(this.subFilterObjectsEnergies[keyAsRangeNumber].enumValue)
      });

      //5) init subFiltersValues
      this.subFiltersValues = {
        vehicleType: {
          ids: JSON.parse(filtersData.vehicles),
          textValueIfOne: filtersData.selectedFilterVehicleTypeNameIfOne,
        },
        brand: {
          ids: JSON.parse(filtersData.brands),
          textValueIfOne: filtersData.selectedFilterBrandNameIfOne,
        },
        model: {
          ids: JSON.parse(filtersData.models),
          higherCategoryIds: JSON.parse(filtersData.modelsHigherCategoryIds),
        },
        modelFinish: {
          ids: JSON.parse(filtersData.modelFinishes),
          higherCategoryIds: JSON.parse(filtersData.modelFinishesHigherCategoryIds),
        },
        color: {
          values: colorsValues,
        },
        energy: {
          values: energiesValues,
        },
      }

      //6) maj de filtersMenuLine.displaySelectedValuesText
      var filterMenuObjectTypesToUpdateSelectedValuesText = ['vehicleType', 'brand', 'model', 'modelFinish', 'color', 'energy']
      for (const objectTypeToUpdateText of filterMenuObjectTypesToUpdateSelectedValuesText) {
        await this.updateSelectedValuesText(objectTypeToUpdateText)
      }
      //celui de localisation est géré dans le watch de localisationSelected
    },

    updateIsSearchCollapsedFollowingCollapsed(newValue) {
      this.isSearchCollapsedFollowingCollapsed = newValue
    },

    openFiltersModalFromValueFromBtnName() {
      if (this.fromBtnName === 'sort') {
        this.openSortFilter()
      } else if (this.fromBtnName === 'localisation') {
        this.openOnlyLocalisationFilter()
      } else if (this.fromBtnName === 'brands') {
        this.openSubFilter(this.filtersMenuObjects.brand)
      } else if (this.fromBtnName === 'vehicles') {
        this.openSubFilter(this.filtersMenuObjects.vehicleType)
      } else if (this.fromBtnName === 'filters') {
        this.openFiltersMenu()
      }
    },

    setSortFilterModal() {
      this.isSortFilter = true
      this.isOnlyLocationFilter = false
      this.isFiltersMenu = false
      this.isSubFilter = false
    },
    setOnlyLocationFilterModal() {
      this.isSortFilter = false
      this.isOnlyLocationFilter = true
      this.isFiltersMenu = false
      this.isSubFilter = false
    },
    setFiltersMenuModal() {
      this.isSortFilter = false
      this.isOnlyLocationFilter = false
      this.isFiltersMenu = true
      this.isSubFilter = false
    },
    setSubFilterModal() {
      this.isSortFilter = false
      this.isOnlyLocationFilter = false
      this.isFiltersMenu = false
      this.isSubFilter = true
    },

    async resetModal() {
      this.showRangeSliders = false
      await this.cleanSubFilterData()
      eventBus.$emit("closedFiltersModal")
    },

    async openSubFilter(filtersMenuLine) {
      await this.refreshSubFilterData(filtersMenuLine.filterObjectType)

      this.subFilterObjectType = filtersMenuLine.filterObjectType
      this.modalTitle = filtersMenuLine.name
      this.setSubFilterModal()
    },

    openSortFilter() {
      this.cleanSubFilterData()
      this.modalTitle = this.defaultSortModalTitle
      this.setSortFilterModal()
    },

    openOnlyLocalisationFilter() {
      this.cleanSubFilterData()
      this.modalTitle = this.defaultLocalisationModalTitle
      this.setOnlyLocationFilterModal()
    },

    openFiltersMenu() {
      this.cleanSubFilterData()
      this.modalTitle = this.defaultModalTitle
      this.setFiltersMenuModal()
    },

    async refreshSubFilterData(filterObjectType) {
      if (filterObjectType === 'color' && this.subFilterObjectType !== 'color') {
        this.subFilterObjects = this.subFilterObjectsColors //init
        this.oneIsVisible = Object.keys(this.subFilterObjectsColors).length > 0

      } else if (filterObjectType === 'energy' && this.subFilterObjectType !== 'energy') {
        this.subFilterObjects = this.subFilterObjectsEnergies //init
        this.oneIsVisible = Object.keys(this.subFilterObjectsEnergies).length > 0

      } else if (filterObjectType === 'color' || filterObjectType === 'energy') {
        this.setIsVisibles()

      } else {
        const data = new FormData()
        data.append('objectType', filterObjectType)
        data.append('subFilterSearch', this.subFilterSearch)

        data.append('subFiltersValues_VehicleTypeIds', this.toStringApiList(this.subFiltersValues.vehicleType.ids))
        data.append('subFiltersValues_BrandIds', this.toStringApiList(this.subFiltersValues.brand.ids))
        data.append('subFiltersValues_ModelIds', this.toStringApiList(this.subFiltersValues.model.ids))
        data.append('subFiltersValues_ModelHigherCategoryIds', this.toStringApiList(this.subFiltersValues.model.higherCategoryIds))
        data.append('subFiltersValues_ModelFinishIds', this.toStringApiList(this.subFiltersValues.modelFinish.ids))
        data.append('subFiltersValues_ModelFinishHigherCategoryIds', this.toStringApiList(this.subFiltersValues.modelFinish.higherCategoryIds))

        await api({
          method: 'POST',
          url: `/search/getSubFilterObjectsForOptions`,
          data: data
        })
          .then((response) => {
            this.subFilterObjects = response.data
          })
      }

      this.refreshCanSelectAll()
    },

    setIsVisibles() {
      this.settingIsVisible = true

      var searchLower = this.subFilterSearch.toLowerCase()

      var newSubFilterObjects = {}
      var newOneIsVisible = false

      //show/hide
      Object.keys(this.subFilterObjects).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        newSubFilterObjects[keyAsRangeNumber] = this.subFilterObjects[keyAsRangeNumber]
        newSubFilterObjects[keyAsRangeNumber].isVisible = newSubFilterObjects[keyAsRangeNumber].name.toLowerCase().includes(searchLower)
        if (newSubFilterObjects[keyAsRangeNumber].isVisible) {
          newOneIsVisible = true
        }

        //+ reload de chaque élément de la liste via sa key pour que ca se mette à jour directement dans la vue (sinon les subFilterLines avec les éléments où isVisible redevient true ne s'affichent pas)
        var enumValue = this.subFilterObjects[keyAsRangeNumber].enumValue
        if (this.subFilterObjectType === 'color') {
          this.subFilterKeysColors[enumValue] += 1
        }
        if (this.subFilterObjectType === 'energy') {
          this.subFilterKeysEnergies[enumValue] += 1
        }
      });

      //reload complet de la liste pour que les nouveaux isVisible s'appliquent directement dans la vue (sinon 1 temps de retard sur chaque caractère de la recherche)
      this.subFilterObjects = newSubFilterObjects
      this.oneIsVisible = newOneIsVisible

      this.settingIsVisible = false
    },

    /**
     * reload complet une première fois les subFilterObjects quand système des enums:
     * permet de bien prendre en compte les changements de valeurs isSelected par la suite,
     * > même si on clique sur un label au lieu du checkbox /
     * > et même si on clique sur un checkbox pour que ce soit appliqué dans l'élément qui est dans la vue
     */
    firstReloadSubFilterObjectsForEnum() {
      if (this.subFilterObjectType !== 'color' && this.subFilterObjectType !== 'energy') {
        return
      }
      if (this.subFilterObjectType === 'color' && this.firstReloadSubFilterObjectsForEnumColorsDone) {
        return
      }
      if (this.subFilterObjectType === 'energy' && this.firstReloadSubFilterObjectsForEnumEnergiesDone) {
        return
      }

      //car pas besoin de changer les keys pendant qu'on reload toute la liste
      this.settingIsVisible = true

      var newSubFilterObjects = {}

      //get all elements
      Object.keys(this.subFilterObjects).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        newSubFilterObjects[keyAsRangeNumber] = this.subFilterObjects[keyAsRangeNumber]
      });

      //reload complet de la liste
      this.subFilterObjects = newSubFilterObjects

      //set first reload done
      if (this.subFilterObjectType === 'color' && !this.firstReloadSubFilterObjectsForEnumColorsDone) {
        this.firstReloadSubFilterObjectsForEnumColorsDone = true
      }
      if (this.subFilterObjectType === 'energy' && !this.firstReloadSubFilterObjectsForEnumEnergiesDone) {
        this.firstReloadSubFilterObjectsForEnumEnergiesDone = true
      }

      this.settingIsVisible = false
    },

    setIsSelected(isSelected) {
      //select or unselect all
      Object.keys(this.subFilterObjects).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        this.subFilterObjects[keyAsRangeNumber].isSelected = isSelected

        //+ reload de chaque élément de la liste via sa key pour que ca se mette à jour directement dans la vue
        var enumValue = this.subFilterObjects[keyAsRangeNumber].enumValue
        if (this.subFilterObjectType === 'color') {
          this.subFilterKeysColors[enumValue] += 1
        }
        if (this.subFilterObjectType === 'energy') {
          this.subFilterKeysEnergies[enumValue] += 1
        }
      });
    },

    refreshCanSelectAll() {
      var newCanSelectAll = false
      Object.keys(this.subFilterObjects).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        if (this.subFilterObjects[keyAsRangeNumber].isVisible && !this.subFilterObjects[keyAsRangeNumber].isSelected) {
          newCanSelectAll = true
        }
      });
      this.canSelectAll = newCanSelectAll
    },

    async cleanSubFilterData() {
      this.subFilterObjectType = null
      this.subFilterObjects = {}
      this.subFilterSearch = ""
      this.oneIsVisible = false
      this.localisationSearch = {
        value: '',
        invalidFeedback: null,
        status: null,
      }
      this.canSelectAll = true
    },

    cleanLocalisationFilter() {
      this.localisationSearch = {
        value: '',
        invalidFeedback: null,
        status: null,
      }
      this.localisationRadius = this.limitMinLocalisationRadius
      this.forceRerenderLocalisationRadiusSliders()

      if (this.localisationSelected.address) {
        //va refresh automatiquement la recherche
        this.localisationSelected = {
          address: null,
          radius: null,
          latitude: null,
          longitude: null,
        }
      }

      this.filtersMenuObjects.localisation.displaySelectedValuesText = null
    },

    async cleanSubFilter() {
      if (this.subFilterObjectType === 'localisation') {
        this.cleanLocalisationFilter()
        return
      }

      //car on clean plusieurs valeurs d'un coup
      this.isUpdatingOrDeletingSeveralFilters = true

      //clean data for objectType
      this.subFilterSearch = ""

      //vider toutes les valeurs
      if (this.subFilterObjectType === 'vehicleType' || this.subFilterObjectType === 'brand' || this.subFilterObjectType === 'model' || this.subFilterObjectType === 'modelFinish') {
        this.subFiltersValues[this.subFilterObjectType].ids = []
      }
      if (this.subFilterObjectType === 'vehicleType' || this.subFilterObjectType === 'brand') {
        this.subFiltersValues[this.subFilterObjectType].textValueIfOne = null
      }
      if (this.subFilterObjectType === 'model' || this.subFilterObjectType === 'modelFinish') {
        this.subFiltersValues[this.subFilterObjectType].higherCategoryIds = []
      }
      if (this.subFilterObjectType === 'color' || this.subFilterObjectType === 'energy') {
        this.subFiltersValues[this.subFilterObjectType].values = []

        //+ tout déselecionner visuellement
        this.setIsSelected(false)
      }
      this.filtersMenuObjects[this.subFilterObjectType].displaySelectedValuesText = null

      //refresh
      await this.refreshSubFilterData(this.subFilterObjectType)

      this.isUpdatingOrDeletingSeveralFilters = false
    },

    cleanAllFilters() {
      this.isUpdatingOrDeletingSeveralFilters = true

      this.cleanLocalisationFilter()
      this.cleanSubFilterData()

      Object.keys(this.filtersMenuObjects).forEach(key => {
        this.filtersMenuObjects[key].displaySelectedValuesText = null
      });

      this.minFiscalPower = this.limitMinFiscalPower
      this.maxFiscalPower = this.limitMaxFiscalPower
      this.forceRerenderFiscalPowerSlider()

      Object.keys(this.subFilterObjectsColors).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        this.subFilterObjectsColors[keyAsRangeNumber].isSelected = false
      });

      Object.keys(this.subFilterObjectsEnergies).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        this.subFilterObjectsEnergies[keyAsRangeNumber].isSelected = false
      });

      this.subFiltersValues = {
        vehicleType: {
          ids: [],
          textValueIfOne: null,
        },
        brand: {
          ids: [],
          textValueIfOne: null,
        },
        model: {
          ids: [],
          higherCategoryIds: [],
        },
        modelFinish: {
          ids: [],
          higherCategoryIds: [],
        },
        color: {
          values: [],
        },
        energy: {
          values: [],
        },
      }

      this.isUpdatingOrDeletingSeveralFilters = false
    },

    updateLocalisationRadius(value) {
      this.localisationRadius = parseInt(value)
      if (this.localisationSelected.address) {
        this.localisationSelected = {
          address: this.localisationSelected.address,
          radius: this.localisationRadius,
          latitude: this.localisationSelected.latitude,
          longitude: this.localisationSelected.longitude,
        }
      }
    },

    /** on affiche le composant une fois qu'il a toute la largeur d'affichage disponible => sinon min et max sont mal initialisés */
    showRangeSlidersWithTotalWidth() {
      setTimeout(() => {
        this.showRangeSliders = true
      }, 300);
    },

    updateMinFiscalPower(min) {
      this.minFiscalPower = parseInt(min)
    },

    updateMaxFiscalPower(max) {
      this.maxFiscalPower = parseInt(max)
    },

    forceRerenderFiscalPowerSlider() {
      this.fiscalPowerSliderKey += 1
    },

    forceRerenderLocalisationRadiusSliders() {
      this.localisationRadiusSliderForSubFilterKey += 1
      this.localisationRadiusSliderForLocationFilterKey += 1
    },

    /*** get valeurs & sélections des checkbox ***/
    async toggleSelectSubFilterElement(subFilterElement, isFromCheckbox) {
      this.firstReloadSubFilterObjectsForEnum()

      //init
      if (!isFromCheckbox) {
        subFilterElement.isSelected = !subFilterElement.isSelected
      }

      var isSelected = subFilterElement.isSelected
      var isEnumMenu = this.filtersMenuObjects[this.subFilterObjectType].isEnumMenu
      var propName = isEnumMenu ? 'enumValue' : 'id'
      var listName = isEnumMenu ? 'values' : 'ids'

      var hasNoSelectedValues = this.subFiltersValues[this.subFilterObjectType][listName].length === 0

      //data pour traitements à la fin
      var filterMenuObjectTypesToUpdateSelectedValuesText = [this.subFilterObjectType]

      //cas impossible si une valeur dans la barre de recherche
      if (subFilterElement.isHigherCategory) {

        var subValuesChanged = []

        //1) set isSelected à true ou false pour tous les enfants
        Object.keys(this.subFilterObjects).forEach(key => {
          var keyAsRangeNumber = parseInt(key)
          if (keyAsRangeNumber !== subFilterElement.rangeOfHigherCategory && this.subFilterObjects[keyAsRangeNumber].rangeOfHigherCategory === subFilterElement.rangeOfHigherCategory) {
            if (this.subFilterObjects[keyAsRangeNumber].isSelected !== isSelected) {
              this.subFilterObjects[keyAsRangeNumber].isSelected = isSelected

              //for 3) & 4)
              subValuesChanged.push(this.subFilterObjects[keyAsRangeNumber][propName])
            }
          }
        });

        //2) add ou remove higherCategoryId dans subFiltersValues.[].higherCategoryIds
        if (isSelected) {
          this.subFiltersValues[this.subFilterObjectType].higherCategoryIds.push(subFilterElement.higherCategoryId)
        } else {
          this.deleteItemInArray(subFilterElement.higherCategoryId, this.subFiltersValues[this.subFilterObjectType].higherCategoryIds)
        }

        //3)
        // add les id/enumValue de tous ceux qui étaient isSelected=false dans subFiltersValues.[].ids/values
        // ou
        // remove de tous ceux qui étaient isSelected=true
        if (isSelected) {
          this.subFiltersValues[this.subFilterObjectType][listName].push(...subValuesChanged)
        } else {
          for (const valueChanged of subValuesChanged) {
            this.deleteItemInArray(valueChanged, this.subFiltersValues[this.subFilterObjectType][listName])
          }
        }

        //4) si on remove, et qu'il reste au moins une valeur sélectionnée (le filtre inter-category de filtre est toujours applicable), enlever tous les ids/higherCategoryIds des subCategories impactées
        var needToCheckInterFilterValues = this.subFilterObjectType === 'model' //seul filtre avec des higherCategories qui impacte les valeurs d'un sous-filtre
        if (needToCheckInterFilterValues && !isSelected) {
          var stealOneSelectedValue = this.subFiltersValues[this.subFilterObjectType][listName].length > 0

          if (stealOneSelectedValue) {
            //tous les model.ids supprimés sont enlevés des modelFinish.higherCategoryIds
            for (const valueChanged of subValuesChanged) {
              this.deleteItemInArray(valueChanged, this.subFiltersValues['modelFinish'].higherCategoryIds)
            }

            //tous les modelFinish.ids qui appartiennent aux model.ids supprimés sont enlevés
            var atLeastOneIsDeleted = await this.deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds('model', 'modelFinish')
            if (atLeastOneIsDeleted) {
              filterMenuObjectTypesToUpdateSelectedValuesText.push('modelFinish')
            }
          }
        }

        //5) si on ajoute alors que rien n'était sélectionné (le filtre inter-category de filtre commence à s'appliquer), enlever tous les ids/higherCategoryIds des subCategories impactées
        if (needToCheckInterFilterValues && isSelected && hasNoSelectedValues) {
          //tous les modelFinish.higherCategoryIds qui n'appartiennent pas aux model.ids ajoutés sont enlevés
          var toDeleteHigherCategoryIds = []
          for (const higherCategoryId of this.subFiltersValues['modelFinish'].higherCategoryIds) {
            if (!subValuesChanged.includes(higherCategoryId)) {
              toDeleteHigherCategoryIds.push(higherCategoryId)
            }
          }
          for (const higherCategoryId of toDeleteHigherCategoryIds) {
            this.deleteItemInArray(higherCategoryId, this.subFiltersValues['modelFinish'].higherCategoryIds)
          }

          //tous les modelFinish.ids qui n'appartiennent pas aux model.ids ajoutés sont enlevés
          var atLeastOneIsDeleted = await this.deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds('model', 'modelFinish')
          if (atLeastOneIsDeleted) {
            filterMenuObjectTypesToUpdateSelectedValuesText.push('modelFinish')
          }
        }

      } else {

        //1) set isSelected à true (si c'était le dernier élément à cocher) ou false (si c'était le premier élément à décocher) pour la higherCategory
        var needToToggleHigherCategory = false
        if (subFilterElement.rangeOfHigherCategory !== null) {

          var nbNotSelected = 0
          Object.keys(this.subFilterObjects).forEach(key => {
            var keyAsRangeNumber = parseInt(key)
            if (keyAsRangeNumber !== subFilterElement.rangeOfHigherCategory && this.subFilterObjects[keyAsRangeNumber].rangeOfHigherCategory === subFilterElement.rangeOfHigherCategory) {
              if (this.subFilterObjects[keyAsRangeNumber].isSelected === false) {
                nbNotSelected += 1
              }
            }
          });
          needToToggleHigherCategory = (isSelected && nbNotSelected === 0) || (!isSelected && nbNotSelected === 1)
        }
        if (needToToggleHigherCategory) {
          this.subFilterObjects[subFilterElement.rangeOfHigherCategory].isSelected = !this.subFilterObjects[subFilterElement.rangeOfHigherCategory].isSelected
        }

        //2) add ou remove higherCategoryId dans subFiltersValues.[].higherCategoryIds
        //cas 1 : où les higherCategories sont dans this.subFiltersObjects
        if (needToToggleHigherCategory) {
          if (isSelected) {
            this.subFiltersValues[this.subFilterObjectType].higherCategoryIds.push(subFilterElement.higherCategoryId)
          } else {
            this.deleteItemInArray(subFilterElement.higherCategoryId, this.subFiltersValues[this.subFilterObjectType].higherCategoryIds)
          }
        }

        //3)
        // add l' id/enumValue si était isSelected=false dans subFiltersValues.[].ids/values
        // ou
        // remove si était isSelected=true
        if (isSelected) {
          this.subFiltersValues[this.subFilterObjectType][listName].push(subFilterElement[propName])
        } else {
          this.deleteItemInArray(subFilterElement[propName], this.subFiltersValues[this.subFilterObjectType][listName])
        }

        //4) si on remove, et qu'il reste au moins une valeur sélectionnée (le filtre inter-category de filtre est toujours applicable), enlever tous les ids/higherCategoryIds des subCategories impactées
        var needToCheckInterFilterValues = this.subFilterObjectType === 'vehicleType' || this.subFilterObjectType === 'brand' || this.subFilterObjectType === 'model'

        if (needToCheckInterFilterValues && !isSelected) {
          var stealOneSelectedValue = this.subFiltersValues[this.subFilterObjectType][listName].length > 0

          if (stealOneSelectedValue) {

            //l'id de brand/model supprimé est enlevé des model/modelFinish.higherCategoryIds
            var subFilterWhereIsHightCategory = this.filtersMenuObjects[this.subFilterObjectType].isHightCategoryFor
            if (subFilterWhereIsHightCategory !== null) {
              this.deleteItemInArray(subFilterElement[propName], this.subFiltersValues[subFilterWhereIsHightCategory].higherCategoryIds)
            }

            //exemple 1 : toutes les brand.ids qui appartiennent au vehicleType.id supprimé, et qui n'appartiennent pas aux vehicleType.ids restants, sont enlevées
            //exemple 2 : tous les modelFinish.ids qui appartiennent au model.id supprimé sont enlevés
            var isFilteringOnObjectTypes = this.filtersMenuObjects[this.subFilterObjectType].isFilteringOnObjectTypes
            if (isFilteringOnObjectTypes !== null) {
              for (const objectTypeToFilter of isFilteringOnObjectTypes) {
                var atLeastOneIsDeleted = await this.deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds(this.subFilterObjectType, objectTypeToFilter)
                if (atLeastOneIsDeleted) {
                  filterMenuObjectTypesToUpdateSelectedValuesText.push(objectTypeToFilter)
                }
              }
            }

          }
        }

        //5) si on ajoute alors que rien n'était sélectionné (le filtre inter-category de filtre commence à s'appliquer), enlever tous les ids/higherCategoryIds des subCategories impactées
        if (needToCheckInterFilterValues && isSelected && hasNoSelectedValues) {
          //l'id de brand/model ajouté filtre sur les model/modelFinish.higherCategoryIds
          var subFilterWhereIsHightCategory = this.filtersMenuObjects[this.subFilterObjectType].isHightCategoryFor
          if (subFilterWhereIsHightCategory !== null) {
            var toDeleteHigherCategoryIds = []
            for (const higherCategoryId of this.subFiltersValues[subFilterWhereIsHightCategory].higherCategoryIds) {
              if (subFilterElement[propName] !== higherCategoryId) {
                toDeleteHigherCategoryIds.push(higherCategoryId)
              }
            }
            for (const higherCategoryId of toDeleteHigherCategoryIds) {
              this.deleteItemInArray(higherCategoryId, this.subFiltersValues[subFilterWhereIsHightCategory].higherCategoryIds)
            }
          }

          //exemple 1 : toutes les brand.ids qui n'appartiennent pas au vehicleType.id ajouté sont enlevées
          //exemple 2 : tous les modelFinish.ids qui n'appartiennent pas au model.id ajouté sont enlevés
          var isFilteringOnObjectTypes = this.filtersMenuObjects[this.subFilterObjectType].isFilteringOnObjectTypes
          if (isFilteringOnObjectTypes !== null) {
            for (const objectTypeToFilter of isFilteringOnObjectTypes) {
              var atLeastOneIsDeleted = await this.deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds(this.subFilterObjectType, objectTypeToFilter)
              if (atLeastOneIsDeleted) {
                filterMenuObjectTypesToUpdateSelectedValuesText.push(objectTypeToFilter)
              }
            }
          }
        }

        //6) add ou remove higherCategoryId dans subFiltersValues.[].higherCategoryIds (bis)
        //cas 2 : où il y a une recherche et les higherCategories ne sont pas dans this.subFiltersObjects
        if (this.subFilterSearch && (this.subFilterObjectType === 'model' || this.subFilterObjectType === 'modelFinish')) {
          var concernedHiddenHigherCategoryIdsToToggle = await this.getConcernedHiddenHigherCategoryIdsToToggle(this.subFilterObjectType, isSelected, [subFilterElement.id])
          if (concernedHiddenHigherCategoryIdsToToggle) {
            if (isSelected) {
              this.subFiltersValues[this.subFilterObjectType].higherCategoryIds.push(concernedHiddenHigherCategoryIdsToToggle[0])
            } else {
              this.deleteItemInArray(concernedHiddenHigherCategoryIdsToToggle[0], this.subFiltersValues[this.subFilterObjectType].higherCategoryIds)
            }
          }
        }

      }

      //maj de filtersMenuLine.displaySelectedValuesText
      for (const objectTypeToUpdateText of filterMenuObjectTypesToUpdateSelectedValuesText) {
        this.updateSelectedValuesText(objectTypeToUpdateText)
      }

      this.refreshCanSelectAll()
    },

    /*** get valeurs & sélections des checkbox BIS : cas où on clique sur (un)select all ***/
    async selectOrUnselectAll() {
      var wantToSelectAll = this.canSelectAll

      //set IsSelected
      var elementsUpdated = []
      var higherCategoriesUpdated = []
      Object.keys(this.subFilterObjects).forEach(key => {
        var keyAsRangeNumber = parseInt(key)
        if (this.subFilterObjects[keyAsRangeNumber].isVisible && this.subFilterObjects[keyAsRangeNumber].isSelected != wantToSelectAll) {
          this.subFilterObjects[keyAsRangeNumber].isSelected = wantToSelectAll

          if (this.subFilterObjects[keyAsRangeNumber].isHigherCategory) {
            higherCategoriesUpdated.push(this.subFilterObjects[keyAsRangeNumber])
          } else {
            elementsUpdated.push(this.subFilterObjects[keyAsRangeNumber])
          }
        }
      });

      var isEnumMenu = this.filtersMenuObjects[this.subFilterObjectType].isEnumMenu
      var propName = isEnumMenu ? 'enumValue' : 'id'
      var listName = isEnumMenu ? 'values' : 'ids'

      var hasNoSelectedValues = this.subFiltersValues[this.subFilterObjectType][listName].length === 0

      //data pour traitements à la fin
      var filterMenuObjectTypesToUpdateSelectedValuesText = [this.subFilterObjectType]


      //1) pour chaque higherCategory impactée: set isSelected à true ou false ne sert à rien car si les higherCategories sont visibles, elles le sont toutes, et elles sont déjà dans la liste des updated
      //Rien à faire

      //2) add ou remove les higherCategoryIds dans subFiltersValues.[].higherCategoryIds
      //cas 1 : où les higherCategories sont dans this.subFiltersObjects
      higherCategoriesUpdated.forEach(higherCategoryElement => {
        if (wantToSelectAll) {
          this.subFiltersValues[this.subFilterObjectType].higherCategoryIds.push(higherCategoryElement.higherCategoryId)
        } else {
          this.deleteItemInArray(higherCategoryElement.higherCategoryId, this.subFiltersValues[this.subFilterObjectType].higherCategoryIds)
        }
      });

      //3)
      // add les id/enumValue si étaient isSelected=false dans subFiltersValues.[].ids/values
      // ou
      // remove si était isSelected=true
      elementsUpdated.forEach(subFilterElement => {
        if (wantToSelectAll) {
          this.subFiltersValues[this.subFilterObjectType][listName].push(subFilterElement[propName])
        } else {
          this.deleteItemInArray(subFilterElement[propName], this.subFiltersValues[this.subFilterObjectType][listName])
        }
      });

      //4) si on remove, et qu'il reste au moins une valeur sélectionnée (le filtre inter-category de filtre est toujours applicable), enlever tous les ids/higherCategoryIds des subCategories impactées
      var needToCheckInterFilterValues = this.subFilterObjectType === 'vehicleType' || this.subFilterObjectType === 'brand' || this.subFilterObjectType === 'model'

      if (needToCheckInterFilterValues && !wantToSelectAll) {
        var stealOneSelectedValue = this.subFiltersValues[this.subFilterObjectType][listName].length > 0

        if (stealOneSelectedValue) {

          //les ids des brand/model supprimés sont enlevés des model/modelFinish.higherCategoryIds
          var subFilterWhereIsHightCategory = this.filtersMenuObjects[this.subFilterObjectType].isHightCategoryFor
          if (subFilterWhereIsHightCategory !== null) {
            elementsUpdated.forEach(subFilterElement => {
              this.deleteItemInArray(subFilterElement[propName], this.subFiltersValues[subFilterWhereIsHightCategory].higherCategoryIds)
            });
          }

          //exemple 1 : toutes les brand.ids qui appartiennent aux vehicleType.id supprimés, et qui n'appartiennent pas aux vehicleType.ids restants, sont enlevées
          //exemple 2 : tous les modelFinish.ids qui appartiennent aux model.id supprimés sont enlevés
          var isFilteringOnObjectTypes = this.filtersMenuObjects[this.subFilterObjectType].isFilteringOnObjectTypes
          if (isFilteringOnObjectTypes !== null) {
            for (const objectTypeToFilter of isFilteringOnObjectTypes) {
              var atLeastOneIsDeleted = await this.deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds(this.subFilterObjectType, objectTypeToFilter)
              if (atLeastOneIsDeleted) {
                filterMenuObjectTypesToUpdateSelectedValuesText.push(objectTypeToFilter)
              }
            }
          }

        }
      }

      //5) si on ajoute alors que rien n'était sélectionné (le filtre inter-category de filtre commence à s'appliquer), enlever tous les ids/higherCategoryIds des subCategories impactées
      if (needToCheckInterFilterValues && wantToSelectAll && hasNoSelectedValues) {
        //les id des brand/model ajoutés filtrent sur les model/modelFinish.higherCategoryIds
        var valuesFromElementsUpdated = elementsUpdated.map((subFilterElement) => (subFilterElement[propName]))

        var subFilterWhereIsHightCategory = this.filtersMenuObjects[this.subFilterObjectType].isHightCategoryFor
        if (subFilterWhereIsHightCategory !== null) {
          var toDeleteHigherCategoryIds = []
          for (const higherCategoryId of this.subFiltersValues[subFilterWhereIsHightCategory].higherCategoryIds) {
            if (!valuesFromElementsUpdated.includes(higherCategoryId)) {
              toDeleteHigherCategoryIds.push(higherCategoryId)
            }
          }
          for (const higherCategoryId of toDeleteHigherCategoryIds) {
            this.deleteItemInArray(higherCategoryId, this.subFiltersValues[subFilterWhereIsHightCategory].higherCategoryIds)
          }
        }

        //exemple 1 : toutes les brand.ids qui n'appartiennent pas aux vehicleType.id ajoutés sont enlevées
        //exemple 2 : tous les modelFinish.ids qui n'appartiennent pas aux model.id ajoutés sont enlevés
        var isFilteringOnObjectTypes = this.filtersMenuObjects[this.subFilterObjectType].isFilteringOnObjectTypes
        if (isFilteringOnObjectTypes !== null) {
          for (const objectTypeToFilter of isFilteringOnObjectTypes) {
            var atLeastOneIsDeleted = await this.deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds(this.subFilterObjectType, objectTypeToFilter)
            if (atLeastOneIsDeleted) {
              filterMenuObjectTypesToUpdateSelectedValuesText.push(objectTypeToFilter)
            }
          }
        }
      }

      //6) add ou remove les higherCategoryIds dans subFiltersValues.[].higherCategoryIds (bis)
      //cas 2 : où il y a une recherche et les higherCategories ne sont pas dans this.subFiltersObjects
      if (this.subFilterSearch && (this.subFilterObjectType === 'model' || this.subFilterObjectType === 'modelFinish')) {
        var valuesFromElementsUpdated = elementsUpdated.map((subFilterElement) => (subFilterElement[propName]))

        var concernedHiddenHigherCategoryIdsToToggle = await this.getConcernedHiddenHigherCategoryIdsToToggle(this.subFilterObjectType, wantToSelectAll, valuesFromElementsUpdated)
        if (concernedHiddenHigherCategoryIdsToToggle) {

          concernedHiddenHigherCategoryIdsToToggle.forEach(higherCategoryId => {
            if (wantToSelectAll) {
              this.subFiltersValues[this.subFilterObjectType].higherCategoryIds.push(higherCategoryId)
            } else {
              this.deleteItemInArray(higherCategoryId, this.subFiltersValues[this.subFilterObjectType].higherCategoryIds)
            }
          });
        }
      }

      //maj de filtersMenuLine.displaySelectedValuesText
      for (const objectTypeToUpdateText of filterMenuObjectTypesToUpdateSelectedValuesText) {
        this.updateSelectedValuesText(objectTypeToUpdateText)
      }

      this.refreshCanSelectAll()
    },

    /**
     * objectTypeHigherFilter => RemainingFilterIds dans le filtre qui vient d'être modifié par l'utilisateur
     * objectTypeSubFilterToFilter => AllLinkedSubFilterIds qui seront supprimés car filtrés par le filtre au-dessus qui vient d'être modifié : objectTypeHigherFilter
     */
    async deleteAllNotLinkedSubFilterIdsFromHigherFilterRemainingIds(objectTypeHigherFilter, objectTypeSubFilterToFilter) {

      var atLeastOneIsDeleted = false

      if (this.subFiltersValues[objectTypeSubFilterToFilter].ids.length === 0) {
        return atLeastOneIsDeleted
      }

      const data = new FormData()
      data.append('higherFilterObjectType', objectTypeHigherFilter)
      data.append('higherFilterIds', this.toStringApiList(this.subFiltersValues[objectTypeHigherFilter].ids))
      data.append('subFilterToFilterObjectType', objectTypeSubFilterToFilter)
      data.append('subFilterToFilterIds', this.toStringApiList(this.subFiltersValues[objectTypeSubFilterToFilter].ids))

      await api({
        method: 'POST',
        url: `/search/getIdsFromListNotLinkedToHigherFilterIds`,
        data: data
      })
        .then((response) => {
          for (const idNotLinked of response.data) {
            this.deleteItemInArray(idNotLinked, this.subFiltersValues[objectTypeSubFilterToFilter].ids)
            atLeastOneIsDeleted = true
          }
        })

      return atLeastOneIsDeleted
    },

    async getConcernedHiddenHigherCategoryIdsToToggle(objectType, isSelected, toggledElementIds) {

      var concernedHiddenHigherCategoryIds = null

      const data = new FormData()
      data.append('objectType', objectType)
      data.append('isSelected', isSelected)
      data.append('toggledElementIds', this.toStringApiList(toggledElementIds))

      data.append('subFiltersValues_VehicleTypeIds', this.toStringApiList(this.subFiltersValues.vehicleType.ids))
      data.append('subFiltersValues_BrandIds', this.toStringApiList(this.subFiltersValues.brand.ids))
      data.append('subFiltersValues_ModelIds', this.toStringApiList(this.subFiltersValues.model.ids))
      data.append('subFiltersValues_ModelHigherCategoryIds', this.toStringApiList(this.subFiltersValues.model.higherCategoryIds))
      data.append('subFiltersValues_ModelFinishIds', this.toStringApiList(this.subFiltersValues.modelFinish.ids))
      data.append('subFiltersValues_ModelFinishHigherCategoryIds', this.toStringApiList(this.subFiltersValues.modelFinish.higherCategoryIds))

      await api({
        method: 'POST',
        url: `/search/getConcernedHiddenHigherCategoryIdsToToggle`,
        data: data
      })
        .then((response) => {
          if (response.data && response.data.length > 0) {
            concernedHiddenHigherCategoryIds = response.data
          }
        })

      return concernedHiddenHigherCategoryIds
    },

    async updateSelectedValuesText(objectType) {

      var isEnumMenu = this.filtersMenuObjects[objectType].isEnumMenu
      var listName = isEnumMenu ? 'values' : 'ids'

      if (this.subFiltersValues[objectType][listName].length === 0) {
        this.filtersMenuObjects[objectType].displaySelectedValuesText = ""

        //+ mise à jour de textValueIfOne
        if (objectType === 'vehicleType' || objectType === 'brand') {
          this.subFiltersValues[objectType].textValueIfOne = null
        }
        return
      }

      const data = new FormData()
      data.append('objectType', objectType)
      data.append('isEnum', isEnumMenu.toString())
      data.append('objectTypeValues', this.toStringApiList(this.subFiltersValues[objectType][listName]))

      await api({
        method: 'POST',
        url: `/search/getDisplaySelectedValuesTextForObjectType`,
        data: data
      })
        .then((response) => {
          var resText
          if (response.data.displaySelectedValuesText) {
            resText = response.data.displaySelectedValuesText
          } else if (response.data.nbSelected > 0) {
            resText = response.data.nbSelected.toString() + " " + i18n.t(response.data.displaySelectedValueCode)
          } else if (response.data.displaySelectedValueCode) {
            resText = i18n.t(response.data.displaySelectedValueCode)
          } else {
            resText = i18n.t(response.data.displaySelectedValuesCode[0])
            if (response.data.displaySelectedValuesCode[1]) {
              resText += ", " + i18n.t(response.data.displaySelectedValuesCode[1])
            }
          }
          this.filtersMenuObjects[objectType].displaySelectedValuesText = resText

          //+ mise à jour de textValueIfOne
          if (objectType === 'vehicleType' || objectType === 'brand') {
            if (this.subFiltersValues[objectType][listName].length === 1) {
              this.subFiltersValues[objectType].textValueIfOne = resText
            } else {
              this.subFiltersValues[objectType].textValueIfOne = null
            }
          }

        })
    },

    searchAnnoncesFromNewFilters() {
      eventBus.$emit("searchAnnoncesFromNewFilters", this.filtersData)
    },

    handleStartAddressInput(value) {
      if (value instanceof InputEvent) {
        this.localisationSearch.value = value.target.value
      } else {
        this.localisationSearch.value = value
      }
      this.localisationSearch.invalidFeedback = null
      this.localisationSearch.status = null
    },

    handleAddressSelect(place) {
      var formattedAddress = null
      var lat = null
      var lng = null
      if (place && place.formatted_address) {
        formattedAddress = place.formatted_address
      }
      if (place && place.geometry && place.geometry.location) {
        lat = place.geometry.location.lat()
        lng = place.geometry.location.lng()
      }
      if (!formattedAddress || !lat || !lng) {
        this.localisationSearch.invalidFeedback = i18n.t('Filters.localisation.error')
        this.localisationSearch.status = false
        return
      }

      this.localisationSearch = {
        value: '',
        invalidFeedback: null,
        status: null,
      }
      this.localisationSelected = {
        address: formattedAddress,
        radius: this.localisationRadius,
        latitude: lat,
        longitude: lng,
      }
    },

    deleteItemInArray(item, array) {
      var indexOfItem = array.indexOf(item)
      if (indexOfItem >= 0) {
        array.splice(array.indexOf(item), 1)
      }
    },

    toStringApiList(list) {
      return JSON.stringify(list)
    },

    debounce(fn, wait) {
      let timer
      return function (...args) {
        if (timer) {
          clearTimeout(timer)
        }
        const context = this
        timer = setTimeout(() => {
          fn.apply(context, args)
        }, wait)
      }
    },

  },
};
</script>

<!-- impossible d'utiliser du css scoped pour une modale => le css doit cibler des éléments ayant des ids ou des classes spécifiques (ex: #filters-modal)-->
<!-- not scoped car modal -->
<style>

#filters-modal .modal-dialog {
  max-width: 420px;
  margin-top: 0px;
  margin-bottom: 0px;
  margin-right: 0px;
  margin-left: auto;
  min-height: 100vh;
  position: relative;
}

#filters-modal .modal-content {
  position: absolute;

  /* border pour le margin de 10px parce que si la fenêtre est pas assez haute, seul le border permet d'avoir un marin-bottom */
  border-style: solid;
  border-color: transparent;
  border-width: 10px;
  border-radius: var(--main-border-radius);

  padding: 30px;
}

#filters-modal .modal-content.total-height {
  min-height: 100%;
  max-height: 100%;
}

#filters-modal .modal-content.thinner-padding {
  padding: 10px 14px 14px;
}

#filters-modal .modal-header, #filters-modal .modal-body, #filters-modal .modal-footer {
  padding: 0;
}

@media (max-width: 420px) {
  #filters-modal .modal-dialog {
    max-width: 100%;
  }
}

#filters-modal .modal-content,
#filters-modal .modal-header,
#filters-modal .modal-body,
#filters-modal .modal-footer {
  background-color: var(--main-black-medium);
}

#filters-modal .modal-body {
  min-height: 100px;
}

/** ----------- animation slide depuis la droite ------------ */
#filters-modal.modal.fade .modal-dialog {
  -webkit-transform: scale(1);
  -moz-transform: scale(1);
  -ms-transform: scale(1);
  transform: scale(1);
  right: -420px;
  opacity: 0;
  -webkit-transition: all 0.3s;
  -moz-transition: all 0.3s;
  transition: all 0.3s;
}

#filters-modal.modal.fade.show .modal-dialog {
  -webkit-transform: scale(1);
  -moz-transform: scale(1);
  -ms-transform: scale(1);
  transform: scale(1);
  -webkit-transform: translate3d(-420px, 0, 0);
  transform: translate3d(-420px, 0, 0);
  opacity: 1;
}

/** ----------- animation slide depuis la droite ------------ */


/** header */
#filters-modal .modal-header-content {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.filters-modal-close-icon, .filters-modal-back-icon {
  cursor: pointer;
  font-size: 28px;
}

.filters-modal-close-icon:hover, .filters-modal-back-icon:hover {
  color: var(--main-grey-medium);
}

/** body */

.filters-menu-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: var(--main-grey-dark);
  padding-top: 18px;
  padding-bottom: 18px;
}

.filters-menu-line:hover {
  cursor: pointer;
  color: var(--main-grey-medium);
}

.special-filter-menu-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 18px;
}

.range-slider-title {
  font-size: 13px;
}

.under-range-slider-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding-bottom: 20px;
}

.filters-menu-line-selected-values {
  color: var(--main-grey-medium);
  margin-left: 20px;
  text-align: right;
}

.filters-menu-line:hover .filters-menu-line-selected-values {
  color: var(--main-grey-dark);
}

@media (min-width: 768px) {
  .filters-menu-line-only-in-mobile-view {
    display: none;
  }
}

/** search bar */
.filters-search-bar .search-input, .filters-search-bar .search-input-append {
  background-color: transparent !important;
  border-color: var(--main-grey-dark) !important;
  color: var(--main-grey-light) !important;
}

.filters-search-bar .search-input {
  position: relative;
  border-radius: var(--fourth-border-radius) !important;
  font-size: var(--content-font-size);
  padding-left: calc(1rem + 20px) !important;
}

.filters-search-bar .search-input-append {
  position: absolute;
  z-index: 2;
  height: 100%;
  border-style: none;
}

.filters-search-bar .search-input.active {
  border-color: var(--main-yellow-medium) !important;
}

.filters-search-bar .search-input.in-error {
  border-color: var(--main-red-medium) !important;
}

.localisation-invalid-feedback {
  font-size: var(--small-font-size);
  color: var(--main-red-medium);
}

/** localisation search autocomplete options */
.pac-container {
  z-index: 1040;
}

/** localisation tag */
.localisation-tag {
  background-color: var(--main-green-lighter);
  color: var(--main-black-darker);
  padding: 10px 14px;
  border-radius: var(--third-border-radius);
  margin-top: 10px;
  margin-bottom: 10px;
  word-break: break-word;
  width: 100%;
}

.localisation-tag-address {
  font-weight: bold;
  line-height: 1.25;
}

.localisation-tag-km {
  line-height: 1.25;
}

/** menu/subFilter scroll & scrollbar inside */
.modal-filters-menu-container {
  max-height: 100%;
  position: absolute;
  height: 100%;
  width: calc(100% + 12px);
}

.modal-sub-filter-container {
  max-height: 100%;
  position: absolute;
  height: 100%;
  width: 100%;
}

.modal-filters-menu-container {
  overflow-x: visible;
}

.sub-filter-list-container {
  overflow-x: hidden;
}

.sub-filter-list-container, .modal-filters-menu-container {
  min-height: 100px;
  overflow-y: auto;
  margin-right: -12px;
  padding-right: 12px;

  /* firefox */
  scrollbar-color: var(--main-black-light) transparent;
  scrollbar-width: thin;
  scrollbar-height: thin;
  scrollbar-radius: 10px;
}

.sub-filter-list-container::-webkit-scrollbar, .modal-filters-menu-container::-webkit-scrollbar {
  -webkit-border-radius: 10px;
  width: 5px;
  height: 5px;
}

.sub-filter-list-container::-webkit-scrollbar-thumb, .modal-filters-menu-container::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: var(--main-black-light);
}

.sub-filter-list-container::-webkit-scrollbar-corner, .modal-filters-menu-container::-webkit-scrollbar-corner {
  background-color: transparent;
}

/** subFilters */
.sub-filter-yellow-action {
  margin-top: 12px;
  color: var(--main-yellow-medium);
  cursor: pointer;
}

.sub-filter-yellow-action:hover {
  color: var(--main-yellow-dark);
}

.sub-filter-list-container .empty-state-img {
  max-width: 50%;
}

.sub-filter-list-no-result-title {
  font-family: Agbalumo-Regular, "Arial", sans-serif;
  font-size: var(--title-level-one-secondary-font-size);
  margin-bottom: 6px;
}

.sub-filter-list-no-result-text {
  font-size: 14px;
  font-weight: lighter;
}

.sub-filter-list-element-container.sub-filter-list-element-container-last {
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: var(--main-grey-dark);
}

.sub-filter-list-element {
  padding-top: 10px;
  padding-bottom: 10px;
}

.sub-filter-list-element-inside {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.sub-filter-list-element-container-higher-category .sub-filter-list-element {
  padding-top: 15px;
  padding-bottom: 15px;
  color: var(--main-yellow-medium);
  font-weight: bold;
}

/** design checkbox */
.sub-filter-list-element-checkbox .custom-control-label::before {
  border-color: var(--main-grey-dark);
}

.sub-filter-list-element-checkbox .custom-control-label::before, .sub-filter-list-element-checkbox .custom-control-label::after {
  width: 1.25rem;
  height: 1.25rem;
  left: -1.25rem;
  top: 0.35rem;
  cursor: pointer;
}

.sub-filter-list-element-checkbox .custom-control-input {
  position: relative;
  height: 1.5rem;
}


/** footer */
#filters-modal .footer-element {
  margin: 0px;
}

</style>



// WEBPACK FOOTER //
// src/views/search/FiltersModal.vue